/**
 * @file src/ui/react/studio/localRail.ts
 * @description What the studio knows about the local audio rail — the props the collection app's
 * `localRailController` hands it. Types only.
 */

export type LocalCardStatus =
  | 'ready'
  | 'analysing'
  | 'permission'
  | 'missing'
  | 'failed'
  | 'deferred'
  /** Being copied into this browser — what makes the file still be here after a reload. */
  | 'copying'
  /** The copy could not be made: playable now, gone after a reload, and the card says so. */
  | 'session-only';

/** The local facts on a drawer card (absent on a hosted card). */
export interface LocalCardState {
  status: LocalCardStatus;
  /** 0..100 while analysing, else null. */
  percent: number | null;
  /** 0..100 while this browser takes its copy, else null. */
  copyPercent: number | null;
  folder: string | null;
  path: string | null;
  durationSec: number | null;
  /** The detected tempo, or null when the analysis found no steady pulse to warp to. */
  tempoBpm: number | null;
  /**
   * How many OTHER collections hold this same file. One file, one copy of the bytes, however many
   * collections — so removing the card here only takes it off the machine when this is the last.
   */
  otherCollections?: number;
}

export type LocalCardAction = 'locate' | 'search-folder' | 'show-path' | 'remove' | 'analyse-now' | 'reanalyse';

export interface LocalRailProps {
  /** Whether local files are offered here at all. False in a room: this machine's files are not
   *  part of a pool everyone can see, so adding and managing them are not shown. */
  enabled: boolean;
  /** `browser` keeps a copy; `desktop` reads the real file; `memory` can keep nothing. */
  platform: 'desktop' | 'browser' | 'memory';
  /** Whether files imported here are copied into this browser at all. */
  copies: boolean;
  /** Bytes those copies use, so the rail can say it without being asked. */
  copiedBytes: number;
  /** How many of this scope's files this browser could not keep. */
  sessionOnly: number;
  total: number;
  missing: number;
  needPermission: number;
  /** The run in progress, for the header's one line — whatever was dropped is analysed. */
  analysing: { done: number; total: number; activeId: string | null } | null;
  missingEntries: Array<{ id: string; name: string; folder: string | null }>;
  missingOpen: boolean;
  /**
   * How THIS MACHINE left the panel: whether it was showing, and the width it was dragged to.
   *
   * Kept with the files rather than in the collection's arrangement, because the panel shows files
   * only this machine has — in the arrangement it was one person's viewing preference stored in
   * everyone's record, and a viewer opening someone else's collection inherited the editor's open
   * panel over an empty list.
   *
   * `panelKnown` is false until the stored record has been read. The studio mounts before that, so
   * it must be able to tell "not read yet" from a deliberate "closed".
   */
  panelKnown: boolean;
  panelOpen: boolean;
  panelWidth: number | null;
  onPanelChange: (next: { open?: boolean; width?: number }) => void;
  onDropFiles: (dataTransfer: DataTransfer, stageIndex: number | null) => void;
  onPickFiles: (stageIndex: number | null) => void;
  onAllowAccess: () => void;
  onAction: (id: string, action: LocalCardAction) => void;
  onSearchFolder: () => void;
  onSetMissingOpen: (open: boolean) => void;
  /**
   * What this collection is called, as the library resolved it — which is the same answer the
   * local-files page shows, so the two never disagree about a name.
   */
  collectionName: string | null;
  /** The key this collection's local files are filed under — what "add to this collection" means. */
  scopeKey: string | null;
  /**
   * Put files this machine already holds into THIS collection's rail. Adding a collection to an
   * entry, never a copy: the bytes are on the disk once, and this is a second place that plays
   * them. It is what the local-files panel exists for.
   */
  onAddToCollection: (ids: string[]) => Promise<void>;
  /**
   * Drop files onto a stage. One stage holds one thing, so the FIRST of `ids` wins and the rest are
   * left alone; the file joins this collection on the way, or it would leave the rail the moment it
   * is swapped out.
   */
  onStageEntries: (ids: string[], stageIndex: number) => Promise<void>;
}

/** True when a drag carries files from the OS (not a card from the rail). */
export function dragCarriesFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false;
  try {
    return Array.from(dataTransfer.types || []).includes('Files');
  } catch {
    return false;
  }
}
